<template>
  <div
    class="min-h-screen relative flex items-baseline lg:items-center justify-center bg-gray-100 pt-40 lg:pt-12 sm:px-8 py-12 px-4"
  >
    <div
      class="max-w-md w-full rounded px-5 sm:px-10 border bg-white relative z-10"
      :class="newMobileScreens ? 'pt-5 pb-8' : 'py-16 shadow'"
    >
      <div class="hidden lg:block">
        <img
          id="login-brand-logo"
          class="brand-logo mx-auto h-12 lg:h-20 md:h-16 w-auto"
          :src="brandLogoUrl"
        />
        <h2
          id="login-header"
          class="mt-6 text-center text-3xl leading-9 font-extrabold text-gray-900"
        >
          Sign in to your account
        </h2>
      </div>
      <div
        class="flex items-center justify-center w-16 h-16 rounded-full border-2 border-white lg:hidden absolute top-0 left-1/2 transform -translate-y-1/2 -translate-x-1/2 fallback-theme-background"
        :style="{ 'background-color': getMobileTheme().secondaryHexCode }"
      >
        <BaseIcon name="scholar" hwClass="w-8 h-8 text-white" />
      </div>
      <form id="login-form" class="mt-8" @submit.prevent="login">
        <input type="hidden" name="remember" value="true" />
        <div class="rounded-md shadow-sm">
          <div>
            <input
              id="email"
              v-model="email"
              aria-label="Email address"
              name="email"
              type="email"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 sm:text-base sm:leading-5 placeholder:text-base placeholder:leading-5"
              placeholder="Email address"
              :style="{ 'text-align-last': 'start' }"
            />
          </div>
          <div class="-mt-px">
            <input
              id="password"
              v-model="password"
              aria-label="Password"
              name="password"
              type="password"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 sm:text-base sm:leading-5 placeholder:text-base placeholder:leading-5"
              placeholder="Password"
              :style="{ 'text-align-last': 'start' }"
            />
          </div>
        </div>
        <div class="mt-2 flex text-red-400" v-if="errorMsg">{{ errorMsg }}</div>
        <div class="mt-6 flex items-center justify-between">
          <div class="flex items-center">
            <input
              v-model="rememberMe"
              id="remember_me"
              type="checkbox"
              class="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
            />
            <label
              for="remember_me"
              class="ml-2 block text-sm leading-5 text-gray-900"
              >Remember me</label
            >
          </div>

          <div class="text-sm leading-5">
            <!-- <a href="#"> </a> -->
            <router-link
              id="forgot-password"
              :to="{ name: 'recovery' }"
              class="font-semibold text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition ease-in-out duration-150"
              >Forgot your password?</router-link
            >
          </div>
        </div>

        <div class="mt-6 flex justify-center">
          <button
            id="login-button"
            :disabled="loader"
            type="submit"
            :class="[
              loader ? 'cursor-not-allowed' : '',
              newMobileScreens
                ? `w-1/3 px-5 py-2 ${getMobileTheme().primary}`
                : 'w-1/2 py-2 px-4 lg:bg-blue-600 lg:hover:bg-blue-500 lg:focus:border-blue-700 active:bg-blue-700 fallback-theme-background',
            ]"
            class="group relative flex justify-center border border-transparent text-sm leading-5 font-medium rounded-md text-white focus:outline-none focus:shadow-outline-indigo transition duration-150 ease-in-out lg:w-full"
          >
            <ButtonLoader v-if="loader" />
            <template v-else>
              <span
                v-if="!newMobileScreens"
                class="absolute left-0 inset-y-0 flex items-center pl-3"
              >
                <BaseIcon name="lockfilled" hwClass="w-3 h-3" />
              </span>
              Sign in
            </template>
          </button>
        </div>
      </form>
    </div>
    <div
      class="flex items-center justify-center pb-16 w-full h-56 absolute top-0 lg:hidden fallback-theme-background"
      :style="{ 'background-color': getMobileTheme().secondaryHexCode }"
    >
      <img
        v-if="brandLogoUrl"
        class="brand-logo h-10 w-auto sm:h-10"
        :src="replaceBucketUrlWithCdnUrl(brandLogoUrl)"
        alt="Logo"
      />
      <p class="font-semibold text-white lg:text-current" v-else>Your Logo</p>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue'
import LoginService from '../services/LoginService'
import ButtonLoader from '@/components/ui-components/ButtonLoader.vue'
import { getMobileTheme } from '@/helper'
import { isMobileTablet, deviceId } from '../helper/device_info'
import {
  initialize,
  saveUserId,
  saveContactId,
  saveFeatureFlag,
} from '@/services'
import BaseIcon from '@/components/svgicons/BaseIcon.vue'
// import { fetchAndSaveBrandData } from '../helper'

export default defineComponent({
  components: { ButtonLoader, BaseIcon },
  data() {
    return {
      email: '',
      password: '',
      deviceType: '',
      deviceId: '',
      rememberMe: false,
      errorMsg: '',
      loader: false,
    }
  },
  computed: {
    brandLogoUrl() {
      return this.$store.state.logoUrl
    },
    hostName(): string {
      return this.$route.query.domain || window.location.hostname
    },
    newMobileScreens(): boolean {
      return this.$store.getters.newMobileScreens
    },
  },
  methods: {
    async login() {
      try {
        this.loader = true
        const { data } = await LoginService.login({
          email: this.email.trim(),
          password: this.password.trim(),
          deviceName: navigator.userAgent,
          deviceId: deviceId,
          deviceType: isMobileTablet() ? 'mobile' : 'desktop',
          hostName: this.hostName,
        })
        if (data.token) {
          this.errorMsg = ''

          const payload = btoa(JSON.stringify(data))
          if (localStorage) {
            localStorage.setItem('cat', payload)
          }

          if (this.rememberMe) {
            this.$cookies.set('cat', payload, '30d')
          } else {
            this.$cookies.set('cat', payload)
          }

          initialize(data.locationId)
          saveUserId(data.userId)
          saveContactId(data.contactId)
          saveFeatureFlag(data.featureFlag)
          this.$emit('updateLoginStatus')
          this.$router.push({ name: 'library-v2' })
        }
      } catch (error) {
        this.errorMsg =
          error.response && error.response.data.msg
            ? error.response.data.msg
            : 'Incorrect username or password'
      } finally {
        this.loader = false
      }
    },
    getMobileTheme,
  },
})
</script>
<style scoped>
.fallback-theme-background {
  background-color: #0072ef;
}
</style>
